/**
 * @fileoverview Multi-select dropdown with search and chip display
 * @module ctl-multi-select
 * @version 1.0.0
 *
 * @description
 * A multi-select component that allows users to select multiple options from a
 * searchable dropdown. Selected items are displayed as removable chips inside
 * the control. Supports keyboard navigation, disabled items, and form integration.
 *
 * @example
 * <ctl-multi-select id="ms" placeholder="Select tags…"></ctl-multi-select>
 * <script type="module">
 *   const ms = document.querySelector('#ms');
 *   ms.items = [
 *     { value: 'js',  label: 'JavaScript' },
 *     { value: 'ts',  label: 'TypeScript' },
 *     { value: 'py',  label: 'Python' },
 *   ];
 *   ms.addEventListener('ctl-change', e => console.log(e.detail.values));
 * </script>
 */

import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement('template');
template.innerHTML = `
<style>
  :host {
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
    width: 100%;
    max-width: 520px;
    position: relative;
  }

  .control {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--ctl-space-1);
    min-height: var(--ctl-input-height, 40px);
    padding: var(--ctl-space-1) var(--ctl-space-2);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface);
    cursor: text;
    transition: border-color var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
    box-sizing: border-box;
  }

  :host([disabled]) .control {
    opacity: var(--ctl-opacity-50, 0.5);
    cursor: not-allowed;
    pointer-events: none;
  }

  .control:focus-within {
    border-color: var(--ctl-color-brand);
    box-shadow: var(--ctl-focus-ring);
    outline: none;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: color-mix(in srgb, var(--ctl-color-brand) 12%, var(--ctl-color-surface));
    border: 1px solid color-mix(in srgb, var(--ctl-color-brand) 30%, transparent);
    border-radius: var(--ctl-radius-full, 999px);
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-brand);
    font-weight: var(--ctl-font-weight-medium, 500);
    max-width: 180px;
    white-space: nowrap;
  }

  .chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chip-remove {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex-shrink: 0;
    opacity: 0.7;
    line-height: 1;
  }
  .chip-remove:hover { opacity: 1; }
  .chip-remove:focus-visible {
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .search {
    all: unset;
    flex: 1;
    min-width: 80px;
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-text);
    background: transparent;
    cursor: text;
    padding: 2px 0;
  }
  .search::placeholder { color: var(--ctl-color-muted); }

  .chevron {
    margin-left: auto;
    flex-shrink: 0;
    color: var(--ctl-color-muted);
    transition: transform var(--ctl-transition-fast);
    pointer-events: none;
  }
  :host([open]) .chevron { transform: rotate(180deg); }

  /* dropdown panel */
  .panel {
    display: none;
    position: fixed;
    z-index: var(--ctl-z-dropdown, 1000);
    background: var(--ctl-color-surface);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    box-shadow: var(--ctl-shadow-md);
    overflow: hidden;
    min-width: 220px;
  }
  :host([open]) .panel { display: block; }

  .panel-search {
    padding: var(--ctl-space-2);
    border-bottom: 1px solid var(--ctl-color-border);
  }

  .panel-search input {
    all: unset;
    width: 100%;
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-text);
    padding: 6px 8px;
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-sm);
    background: var(--ctl-color-surface-2, var(--ctl-color-surface));
    box-sizing: border-box;
  }
  .panel-search input:focus {
    outline: none;
    border-color: var(--ctl-color-brand);
    box-shadow: var(--ctl-focus-ring);
  }

  .list {
    overflow-y: auto;
    max-height: 220px;
  }

  .opt {
    all: unset;
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    width: 100%;
    padding: var(--ctl-space-2) var(--ctl-space-3);
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    cursor: pointer;
    box-sizing: border-box;
    border-radius: 0;
  }
  .opt:hover,
  .opt[data-active="true"] {
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface));
  }
  .opt[aria-disabled="true"] {
    opacity: var(--ctl-opacity-40, 0.4);
    pointer-events: none;
  }
  .opt-check {
    width: 16px;
    height: 16px;
    accent-color: var(--ctl-color-brand);
    flex-shrink: 0;
  }
  .opt-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .empty {
    padding: var(--ctl-space-3);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    text-align: center;
  }

  .panel-footer {
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-muted);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .clear-btn {
    all: unset;
    cursor: pointer;
    color: var(--ctl-color-brand);
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-medium, 500);
  }
  .clear-btn:hover { text-decoration: underline; }
  .clear-btn:focus-visible { outline: 2px solid var(--ctl-color-brand); border-radius: 2px; }

  .sr-only {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
    white-space: nowrap;
    border: 0;
  }
</style>

<div class="control" part="control" role="combobox" aria-haspopup="listbox" aria-expanded="false">
  <span class="chips-container"></span>
  <input class="search" type="text" aria-autocomplete="list" aria-label="Search options" autocomplete="off" spellcheck="false">
  <svg class="chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
</div>

<div class="panel" aria-label="Options">
  <div class="panel-search" hidden>
    <input type="text" placeholder="Search…" aria-label="Filter options" autocomplete="off" spellcheck="false">
  </div>
  <div class="list" role="listbox" aria-multiselectable="true" aria-label="Options"></div>
  <div class="empty" hidden>No results</div>
  <div class="panel-footer">
    <span class="selection-count"></span>
    <button class="clear-btn" type="button" hidden>Clear all</button>
  </div>
</div>

<div class="sr-only" aria-live="polite" aria-atomic="true"></div>
`;

let _msIdCounter = 0;

export class CtlMultiSelect extends HTMLElement {
  static get observedAttributes() {
    return ['placeholder', 'disabled', 'max', 'searchable', 'open'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));

    this._idBase = `ctl-ms-${++_msIdCounter}`;
    this._items = [];
    this._filtered = [];
    this._selected = new Set(); // Set of value strings
    this._activeIndex = -1;
    this._query = '';

    this._control = this.shadowRoot.querySelector('.control');
    this._chipsContainer = this.shadowRoot.querySelector('.chips-container');
    this._searchInput = this.shadowRoot.querySelector('.search');
    this._panel = this.shadowRoot.querySelector('.panel');
    this._panelSearch = this.shadowRoot.querySelector('.panel-search input');
    this._panelSearchWrap = this.shadowRoot.querySelector('.panel-search');
    this._list = this.shadowRoot.querySelector('.list');
    this._empty = this.shadowRoot.querySelector('.empty');
    this._footer = this.shadowRoot.querySelector('.panel-footer');
    this._countEl = this.shadowRoot.querySelector('.selection-count');
    this._clearBtn = this.shadowRoot.querySelector('.clear-btn');
    this._live = this.shadowRoot.querySelector('[aria-live]');

    // Bound references stored for proper add/remove listener pairing
    this._boundDocClick = this._onDocClick.bind(this);
    this._boundGlobalKeyDown = this._onGlobalKeyDown.bind(this);
  }

  connectedCallback() {
    this._control.addEventListener('click', this._onControlClick.bind(this));
    this._searchInput.addEventListener('input', this._onSearchInput.bind(this));
    this._searchInput.addEventListener('keydown', this._onKeyDown.bind(this));
    this._searchInput.addEventListener('focus', () => this.open || this.show());

    this._panelSearch.addEventListener('input', e => {
      this._query = e.target.value;
      this._filterAndRender();
    });
    this._panelSearch.addEventListener('keydown', this._onKeyDown.bind(this));

    this._list.addEventListener('mouseover', this._onOptionHover.bind(this));
    this._list.addEventListener('mousedown', e => e.preventDefault());
    this._list.addEventListener('click', this._onOptionClick.bind(this));

    this._clearBtn.addEventListener('click', () => this._clearAll());

    document.addEventListener('click', this._boundDocClick, true);
    document.addEventListener('keydown', this._boundGlobalKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundDocClick, true);
    document.removeEventListener('keydown', this._boundGlobalKeyDown);
  }

  attributeChangedCallback(name) {
    if (name === 'open') {
      const isOpen = this.hasAttribute('open');
      this._control.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        this._filterAndRender();
        this._positionPanel();
        if (this.searchable) {
          this._panelSearchWrap.hidden = false;
          this._panelSearch.focus();
        }
      } else {
        this._activeIndex = -1;
        this._query = '';
        this._panelSearch.value = '';
        this._panelSearchWrap.hidden = true;
      }
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  get items() { return this._items; }
  set items(val) {
    this._items = Array.isArray(val) ? val : [];
    this._filterAndRender();
    this._renderChips();
    this._updateFooter();
  }

  get values() { return [...this._selected]; }
  set values(arr) {
    this._selected = new Set((Array.isArray(arr) ? arr : []).map(String));
    this._renderChips();
    this._renderList();
    this._updateFooter();
  }

  get disabled() { return this.hasAttribute('disabled'); }
  get open() { return this.hasAttribute('open'); }
  get searchable() { return !this.hasAttribute('searchable') || this.getAttribute('searchable') !== 'false'; }
  get placeholder() { return this.getAttribute('placeholder') || 'Select…'; }
  get max() { return this.hasAttribute('max') ? parseInt(this.getAttribute('max'), 10) : Infinity; }

  show() {
    if (this.disabled) return;
    this.setAttribute('open', '');
  }

  hide() {
    this.removeAttribute('open');
    this._searchInput.value = '';
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _onControlClick(e) {
    if (this.disabled) return;
    if (e.target.closest('.chip-remove')) return;
    if (this.open) {
      this._searchInput.focus();
    } else {
      this.show();
      requestAnimationFrame(() => {
        if (this.searchable) this._panelSearch.focus();
        else this._searchInput.focus();
      });
    }
  }

  _onSearchInput(e) {
    const q = e.target.value;
    if (!this.open) this.show();
    // Inline search syncs to panel search
    this._query = q;
    this._panelSearch.value = q;
    this._filterAndRender();
  }

  _onKeyDown(e) {
    if (this.disabled) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.hide();
      this._searchInput.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this.open) this.show();
      this._moveActive(+1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._moveActive(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!this.open) { this.show(); return; }
      const item = this._filtered[this._activeIndex];
      if (item && !item.disabled) this._toggleItem(item);
      return;
    }
    if (e.key === 'Backspace' && !e.target.value && this._selected.size > 0) {
      // Remove last chip
      const last = [...this._selected].pop();
      this._selected.delete(last);
      this._renderChips();
      this._renderList();
      this._updateFooter();
      this._emitChange();
      return;
    }
    if (e.key === 'Tab') {
      this.hide();
    }
  }

  _onGlobalKeyDown(e) {
    if (e.key === 'Escape' && this.open) {
      this.hide();
      this._searchInput.focus();
    }
  }

  _onDocClick(e) {
    if (!this.open) return;
    if (this.contains(e.target) || this.shadowRoot.contains(e.target)) return;
    if (this._panel.contains(e.target)) return;
    this.hide();
  }

  _onOptionHover(e) {
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    const idx = parseInt(opt.dataset.index, 10);
    if (!isNaN(idx)) this._setActiveIndex(idx);
  }

  _onOptionClick(e) {
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    const idx = parseInt(opt.dataset.index, 10);
    if (!isNaN(idx)) {
      const item = this._filtered[idx];
      if (item && !item.disabled) this._toggleItem(item);
    }
  }

  _toggleItem(item) {
    const v = String(item.value);
    if (this._selected.has(v)) {
      this._selected.delete(v);
    } else {
      if (this._selected.size >= this.max) {
        this._live.textContent = `Maximum ${this.max} items selected.`;
        return;
      }
      this._selected.add(v);
    }
    this._renderChips();
    this._renderList();
    this._updateFooter();
    this._emitChange();
  }

  _clearAll() {
    this._selected.clear();
    this._renderChips();
    this._renderList();
    this._updateFooter();
    this._emitChange();
  }

  _filterAndRender() {
    const q = this._query.trim().toLowerCase();
    const all = Array.isArray(this._items) ? this._items : [];

    this._filtered = (q
      ? all.filter(it => String(it.label ?? it.value ?? '').toLowerCase().includes(q))
      : all
    ).map(it => ({
      value: String(it.value),
      label: String(it.label ?? it.value),
      disabled: !!it.disabled,
    }));

    this._renderList();
  }

  _renderList() {
    this._list.innerHTML = '';

    if (!this._filtered.length) {
      this._empty.hidden = false;
      return;
    }
    this._empty.hidden = true;

    this._filtered.forEach((it, idx) => {
      const checked = this._selected.has(it.value);
      const opt = document.createElement('div');
      opt.className = 'opt';
      opt.role = 'option';
      opt.id = `${this._idBase}-opt-${idx}`;
      opt.setAttribute('role', 'option');
      opt.setAttribute('aria-selected', String(checked));
      opt.setAttribute('aria-disabled', it.disabled ? 'true' : 'false');
      opt.dataset.index = String(idx);
      opt.innerHTML = `
        <input class="opt-check" type="checkbox" tabindex="-1" aria-hidden="true" ${checked ? 'checked' : ''} ${it.disabled ? 'disabled' : ''}>
        <span class="opt-label">${_escapeHtml(it.label)}</span>
      `;
      this._list.appendChild(opt);
    });

    this._paintActive();
  }

  _renderChips() {
    this._chipsContainer.innerHTML = '';
    const selectedItems = this._items.filter(it => this._selected.has(String(it.value)));

    selectedItems.forEach(it => {
      const chip = document.createElement('span');
      chip.className = 'chip';
      chip.innerHTML = `
        <span class="chip-label">${_escapeHtml(String(it.label ?? it.value))}</span>
        <button class="chip-remove" type="button" aria-label="Remove ${_escapeHtml(String(it.label ?? it.value))}" data-value="${_escapeHtml(String(it.value))}">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      chip.querySelector('.chip-remove').addEventListener('click', e => {
        e.stopPropagation();
        this._selected.delete(e.currentTarget.dataset.value);
        this._renderChips();
        this._renderList();
        this._updateFooter();
        this._emitChange();
      });
      this._chipsContainer.appendChild(chip);
    });

    // Update placeholder visibility
    this._searchInput.placeholder = this._selected.size === 0 ? this.placeholder : '';
  }

  _updateFooter() {
    const count = this._selected.size;
    this._countEl.textContent = count === 0 ? '' : `${count} selected`;
    this._clearBtn.hidden = count === 0;
  }

  _emitChange() {
    const values = [...this._selected];
    const items = this._items.filter(it => this._selected.has(String(it.value)));
    this._live.textContent = `${values.length} item${values.length !== 1 ? 's' : ''} selected.`;
    dispatchEvent(this, 'ctl-change', { values, items });
  }

  _moveActive(delta) {
    if (!this._filtered.length) return;
    const n = this._filtered.length;
    let i = this._activeIndex;
    for (let step = 0; step < n; step++) {
      i = (i < 0 ? (delta > 0 ? 0 : n - 1) : (i + delta + n) % n);
      if (!this._filtered[i]?.disabled) { this._setActiveIndex(i); return; }
    }
  }

  _setActiveIndex(i) {
    this._activeIndex = (i >= 0 && i < this._filtered.length) ? i : -1;
    this._paintActive();
    this._scrollActiveIntoView();
  }

  _paintActive() {
    [...this._list.children].forEach((el, i) => {
      el.dataset.active = String(i === this._activeIndex);
    });
  }

  _scrollActiveIntoView() {
    const el = this.shadowRoot.getElementById(`${this._idBase}-opt-${this._activeIndex}`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }

  _positionPanel() {
    const rect = this._control.getBoundingClientRect();
    const panel = this._panel;
    panel.style.left = '-9999px';
    panel.style.top = '-9999px';
    panel.style.minWidth = `${Math.max(220, Math.round(rect.width))}px`;

    const ph = panel.offsetHeight || 300;
    const margin = 8;
    const offset = 4;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.left;
    let top = rect.bottom + offset;

    if (top + ph + margin > vh) top = rect.top - ph - offset;

    left = Math.max(margin, Math.min(left, vw - (rect.width || 220) - margin));
    top = Math.max(margin, top);

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  }
}

function _escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

if (!customElements.get('ctl-multi-select')) {
  customElements.define('ctl-multi-select', CtlMultiSelect);
}
